import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { MapPin, MessageSquare, FileUp, ChevronLeft, Check, Calendar as CalendarIcon, Plus, X, Save, Clock, AlertCircle } from 'lucide-react';
import LocationMap from '../components/LocationMap';
import OrderNotes from '../components/OrderNotes';
import { useAuth } from '../lib/AuthContext';

interface Auftrag {
  id: string;
  auftragsnummer: string;
  internal_number: string;
  kostenstelle?: string;
  sap_nummer?: string;
  auftragsbezeichnung: string;
  auftragsdatum: string;
  monteur_id: string;
  kunde_id: string;
  standort_id: string;
  status: 'neu' | 'begonnen' | 'abgeschlossen' | 'abgerechnet';
  begonnen_am: string | null;
  abgeschlossen_am: string | null;
  abgerechnet_am: string | null;
  pdf_name?: string;
  pdf_data?: string;
}

interface Kunde {
  id: string;
  name: string;
}

interface Standort {
  id: string;
  name: string;
  adresse: string;
}

interface Monteur {
  id: string;
  name: string;
}

interface TerminFormData {
  title: string;
  description: string;
  start_time: string;
  end_time: string;
}

interface CompletionConfirmation {
  show: boolean;
  auftragId: string | null;
  isConfirmed: boolean;
  hasWorksheet: boolean;
}

function AuftragsDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const [auftrag, setAuftrag] = useState<Auftrag | null>(null);
  const [kunde, setKunde] = useState<Kunde | null>(null);
  const [standort, setStandort] = useState<Standort | null>(null);
  const [monteur, setMonteur] = useState<Monteur | null>(null);
  const [loading, setLoading] = useState(true);
  const [showMap, setShowMap] = useState(false);
  const [statusUpdateError, setStatusUpdateError] = useState<string | null>(null);
  const [showNotes, setShowNotes] = useState(false);
  const [showTerminForm, setShowTerminForm] = useState(false);
  const [completionConfirm, setCompletionConfirm] = useState<CompletionConfirmation>({
    show: false,
    auftragId: null,
    isConfirmed: false,
    hasWorksheet: false,
  });
  
  const [terminFormData, setTerminFormData] = useState<TerminFormData>({
    title: '',
    description: '',
    start_time: new Date().toISOString().split('.')[0],
    end_time: new Date(Date.now() + 60 * 60 * 1000).toISOString().split('.')[0],
  });

  useEffect(() => {
    if (id) {
      loadAuftrag(id);
    }
  }, [id]);

  async function loadAuftrag(auftragId: string) {
    try {
      setLoading(true);
      
      const { data: auftragData, error: auftragError } = await supabase
        .from('auftraege')
        .select('*')
        .eq('id', auftragId)
        .single();

      if (auftragError) throw auftragError;
      if (!auftragData) {
        navigate('/bearbeitung');
        return;
      }

      setAuftrag(auftragData);

      // Load related data
      const [kundeRes, standortRes, monteurRes] = await Promise.all([
        supabase
          .from('kunden')
          .select('id, name')
          .eq('id', auftragData.kunde_id)
          .single(),
        supabase
          .from('standorte')
          .select('id, name, adresse')
          .eq('id', auftragData.standort_id)
          .single(),
        supabase
          .from('monteure')
          .select('id, name')
          .eq('id', auftragData.monteur_id)
          .single()
      ]);

      if (kundeRes.data) setKunde(kundeRes.data);
      if (standortRes.data) setStandort(standortRes.data);
      if (monteurRes.data) setMonteur(monteurRes.data);

    } catch (error) {
      console.error('Error loading order:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleTerminSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!auftrag || !monteur) return;

    try {
      const { error } = await supabase
        .from('termine')
        .insert([{
          title: terminFormData.title,
          description: terminFormData.description,
          start_time: terminFormData.start_time,
          end_time: terminFormData.end_time,
          monteur_id: monteur.id,
          auftrag_id: auftrag.id,
          created_by: (await supabase.auth.getUser()).data.user?.id
        }]);

      if (error) throw error;

      setShowTerminForm(false);
      setTerminFormData({
        title: '',
        description: '',
        start_time: new Date().toISOString().split('.')[0],
        end_time: new Date(Date.now() + 60 * 60 * 1000).toISOString().split('.')[0],
      });

      alert('Termin wurde erfolgreich erstellt!');
    } catch (error) {
      console.error('Error creating appointment:', error);
      alert('Fehler beim Erstellen des Termins');
    }
  };

  const handleTerminInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setTerminFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const updateAuftragStatus = async (auftragId: string, newStatus: Auftrag['status']) => {
    try {
      setStatusUpdateError(null);

      // For completion, show confirmation dialog
      if (newStatus === 'abgeschlossen' && userRole !== 'admin') {
        setCompletionConfirm({
          show: true,
          auftragId,
          isConfirmed: false,
          hasWorksheet: false,
        });
        return;
      }

      // For other status changes or admin users, proceed directly
      await performStatusUpdate(auftragId, newStatus);
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Status:', error);
      setStatusUpdateError('Fehler beim Aktualisieren des Status. Bitte versuchen Sie es erneut.');
    }
  };

  const performStatusUpdate = async (auftragId: string, newStatus: Auftrag['status']) => {
    try {
      setStatusUpdateError(null);
      const now = new Date().toISOString();
      const updates: Record<string, any> = {
        status: newStatus,
      };

      // Set the appropriate timestamp based on status
      if (newStatus === 'begonnen') {
        updates.begonnen_am = now;
      } else if (newStatus === 'abgeschlossen') {
        updates.abgeschlossen_am = now;
      } else if (newStatus === 'abgerechnet') {
        updates.abgerechnet_am = now;
      }

      const { data, error } = await supabase
        .from('auftraege')
        .update(updates)
        .eq('id', auftragId)
        .select()
        .single();

      if (error) throw error;

      // Update local state
      if (data) {
        setAuftrag(data);
      }

      // Reset completion confirmation
      setCompletionConfirm({
        show: false,
        auftragId: null,
        isConfirmed: false,
        hasWorksheet: false,
      });
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Status:', error);
      setStatusUpdateError('Fehler beim Aktualisieren des Status. Bitte versuchen Sie es erneut.');
    }
  };

  const handleCompletionConfirm = () => {
    if (!completionConfirm.auftragId) return;
    
    // Check if both checkboxes are checked
    if (!completionConfirm.isConfirmed || !completionConfirm.hasWorksheet) {
      setStatusUpdateError('Bitte bestätigen Sie beide Checkboxen, bevor Sie den Auftrag abschließen.');
      return;
    }
    
    // Proceed with status update
    performStatusUpdate(completionConfirm.auftragId, 'abgeschlossen');
  };

  const cancelCompletionConfirm = () => {
    setCompletionConfirm({
      show: false,
      auftragId: null,
      isConfirmed: false,
      hasWorksheet: false,
    });
    setStatusUpdateError(null);
  };

  const openPDF = (pdfData: string | undefined) => {
    if (pdfData) {
      const newWindow = window.open();
      if (newWindow) {
        newWindow.document.write(`
          <iframe
            src="${pdfData}"
            style="width:100%; height:100vh; border:none;"
          ></iframe>
        `);
      }
    }
  };

  const getStatusColor = (status: Auftrag['status']) => {
    switch (status) {
      case 'neu':
        return 'bg-blue-100 text-blue-800';
      case 'begonnen':
        return 'bg-yellow-100 text-yellow-800';
      case 'abgeschlossen':
        return 'bg-green-100 text-green-800';
      case 'abgerechnet':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: Auftrag['status']) => {
    switch (status) {
      case 'neu':
        return 'Neu';
      case 'begonnen':
        return 'In Bearbeitung';
      case 'abgeschlossen':
        return 'Abgeschlossen';
      case 'abgerechnet':
        return 'Abgerechnet';
      default:
        return status;
    }
  };

  if (loading) {
    return <div className="text-center py-8">Laden...</div>;
  }

  if (!auftrag || !kunde || !standort || !monteur) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center py-8 text-gray-500">
          Auftrag nicht gefunden
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-start mb-6">
          <button
            onClick={() => navigate('/bearbeitung')}
            className="flex items-center text-gray-600 hover:text-gray-800"
          >
            <ChevronLeft className="h-5 w-5" />
            <span>Zurück zur Übersicht</span>
          </button>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(auftrag.status)}`}>
            {getStatusLabel(auftrag.status)}
          </span>
        </div>

        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">{standort.name}</h1>
            <p className="text-gray-600">{auftrag.internal_number}</p>
          </div>

          {statusUpdateError && (
            <div className="p-4 bg-red-50 text-red-700 rounded-md border border-red-200">
              {statusUpdateError}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <p><strong>Datum:</strong> {new Date(auftrag.auftragsdatum).toLocaleDateString()}</p>
                <p><strong>Kunde:</strong> {kunde.name}</p>
                <p><strong>Standort:</strong> {standort.name}</p>
                <p><strong>Adresse:</strong> {standort.adresse}</p>
                <p><strong>Monteur:</strong> {monteur.name}</p>
                <p><strong>Bestellnummer:</strong> {auftrag.internal_number}</p>
                <p><strong>Auftragsnummer:</strong> {auftrag.auftragsnummer}</p>
                {auftrag.kostenstelle && (
                  <p><strong>Kostenstelle:</strong> {auftrag.kostenstelle}</p>
                )}
                {auftrag.sap_nummer && (
                  <p><strong>SAP-Nummer:</strong> {auftrag.sap_nummer}</p>
                )}
                <p><strong>Beschreibung:</strong> {auftrag.auftragsbezeichnung}</p>
              </div>

              <div className="space-y-2">
                {auftrag.begonnen_am && (
                  <p className="text-sm text-gray-500">
                    Begonnen am: {new Date(auftrag.begonnen_am).toLocaleString()}
                  </p>
                )}
                {auftrag.abgeschlossen_am && (
                  <p className="text-sm text-gray-500">
                    Abgeschlossen am: {new Date(auftrag.abgeschlossen_am).toLocaleString()}
                  </p>
                )}
                {auftrag.abgerechnet_am && (
                  <p className="text-sm text-gray-500">
                    Abgerechnet am: {new Date(auftrag.abgerechnet_am).toLocaleString()}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                {/* Action buttons */}
                <div className="flex flex-wrap gap-2">
                  {/* Map toggle */}
                  <button
                    onClick={() => setShowMap(!showMap)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200"
                  >
                    <MapPin size={20} />
                    {showMap ? 'Karte ausblenden' : 'Karte anzeigen'}
                  </button>

                  {/* Notes toggle */}
                  <button
                    onClick={() => setShowNotes(!showNotes)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200"
                  >
                    <MessageSquare size={20} />
                    {showNotes ? 'Notizen ausblenden' : 'Notizen anzeigen'}
                  </button>

                  {/* Calendar button */}
                  <button
                    onClick={() => setShowTerminForm(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200"
                  >
                    <CalendarIcon size={20} />
                    Termin hinzufügen
                  </button>

                  {/* PDF button */}
                  {auftrag.pdf_data && (
                    <button
                      onClick={() => openPDF(auftrag.pdf_data)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200"
                    >
                      <FileUp size={20} />
                      PDF Auftrag öffnen
                      {auftrag.pdf_name && ` (${auftrag.pdf_name})`}
                    </button>
                  )}
                </div>

                {/* Status update buttons */}
                {userRole === 'admin' && (
                  <div className="space-y-2 mt-4">
                    {auftrag.status === 'neu' && (
                      <button
                        onClick={() => updateAuftragStatus(auftrag.id, 'begonnen')}
                        className="w-full bg-yellow-100 text-yellow-800 px-4 py-2 rounded flex items-center justify-center gap-2 hover:bg-yellow-200"
                      >
                        <Clock size={20} />
                        In Bearbeitung setzen
                      </button>
                    )}
                    
                    {auftrag.status === 'begonnen' && (
                      <button
                        onClick={() => updateAuftragStatus(auftrag.id, 'abgeschlossen')}
                        className="w-full bg-green-100 text-green-800 px-4 py-2 rounded flex items-center justify-center gap-2 hover:bg-green-200"
                      >
                        <Check size={20} />
                        Als abgeschlossen markieren
                      </button>
                    )}
                    
                    {auftrag.status === 'abgeschlossen' && (
                      <button
                        onClick={() => updateAuftragStatus(auftrag.id, 'abgerechnet')}
                        className="w-full bg-purple-100 text-purple-800 px-4 py-2 rounded flex items-center justify-center gap-2 hover:bg-purple-200"
                      >
                        <Check size={20} />
                        Als abgerechnet markieren
                      </button>
                    )}

                    {/* Allow reverting status */}
                    {auftrag.status !== 'neu' && (
                      <button
                        onClick={() => updateAuftragStatus(auftrag.id, 'neu')}
                        className="w-full bg-blue-100 text-blue-800 px-4 py-2 rounded flex items-center justify-center gap-2 hover:bg-blue-200"
                      >
                        <Clock size={20} />
                        Zurück auf "Neu" setzen
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Map */}
              {showMap && (
                <div className="mt-4">
                  <LocationMap address={standort.adresse} name={standort.name} />
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {showNotes && (
            <div className="mt-4">
              <OrderNotes auftragId={auftrag.id} />
            </div>
          )}
        </div>
      </div>

      {/* Completion confirmation dialog */}
      {completionConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <AlertCircle className="text-yellow-600" size={24} />
              Auftrag abschließen
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-start gap-2 p-3 rounded-md hover:bg-gray-50">
                <div className="flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    id="confirmComplete"
                    checked={completionConfirm.isConfirmed}
                    onChange={(e) => setCompletionConfirm(prev => ({
                      ...prev,
                      isConfirmed: e.target.checked
                    }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
                <label htmlFor="confirmComplete" className="cursor-pointer">
                  <div className="font-medium">Auftrag abschließen bestätigen</div>
                  <div className="text-sm text-gray-500">
                    Ich bestätige, dass alle Arbeiten vollständig und ordnungsgemäß ausgeführt wurden.
                  </div>
                </label>
              </div>
              
              <div className="flex items-start gap-2 p-3 rounded-md hover:bg-gray-50">
                <div className="flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    id="confirmWorksheet"
                    checked={completionConfirm.hasWorksheet}
                    onChange={(e) => setCompletionConfirm(prev => ({
                      ...prev,
                      hasWorksheet: e.target.checked
                    }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
                <label htmlFor="confirmWorksheet" className="cursor-pointer">
                  <div className="font-medium">Arbeitszettel ausgefüllt</div>
                  <div className="text-sm text-gray-500">
                    Ich bestätige, dass ich den Arbeitszettel vollständig ausgefüllt habe.
                  </div>
                </label>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={cancelCompletionConfirm}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                Abbrechen
              </button>
              <button
                onClick={handleCompletionConfirm}
                disabled={!completionConfirm.isConfirmed || !completionConfirm.hasWorksheet}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Check size={16} />
                Auftrag abschließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calendar form modal */}
      {showTerminForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <CalendarIcon className="text-blue-600" />
              Termin hinzufügen
            </h2>

            <form onSubmit={handleTerminSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Titel
                </label>
                <input
                  type="text"
                  name="title"
                  value={terminFormData.title}
                  onChange={handleTerminInputChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="z.B. Wartung durchführen"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Startzeit
                  </label>
                  <input
                    type="datetime-local"
                    name="start_time"
                    value={terminFormData.start_time}
                    onChange={handleTerminInputChange}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Endzeit
                  </label>
                  <input
                    type="datetime-local"
                    name="end_time"
                    value={terminFormData.end_time}
                    onChange={handleTerminInputChange}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Beschreibung (optional)
                </label>
                <textarea
                  name="description"
                  value={terminFormData.description}
                  onChange={handleTerminInputChange}
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Zusätzliche Informationen zum Termin"
                />
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowTerminForm(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center gap-2"
                >
                  <X size={20} />
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
                >
                  <Save size={20} />
                  Termin speichern
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AuftragsDetails;